import React, { useMemo, useState } from 'react';
import { Flex, Tag, Typography, theme } from 'antd';
import { CheckCircleOutlined, CloseCircleOutlined, MinusCircleOutlined } from '@ant-design/icons';

import type { DiagnosticsReportResponse } from '../api/diagnosticsReportApi';
import { blockedCrawlerTagStyle } from './blockedCrawlerTagStyle';
import { reportCardChrome } from './reportCardStyle';
import CheckRowFixPanel, { CheckRowFixToggle } from './CheckRowFixPanel';
import { findFixTip, resolveFixTip } from './diagnosticsFixTips';
import {
  type CheckRow,
  extractDomain,
  getBlockedProviders,
  getCrawlersTested,
} from '../diagnosticsReportUtils';
import type useTrackDiagnosticsEvent from '../hooks/useTrackDiagnosticsEvent';

// Width of the first column (the check name, and the "Check Details" label above it).
// Exported so the column-label row in DiagnosticsReportView lines up with the cards.
export const CHECK_ROW_LABEL_BASIS = '45%';

type DiagnosticsCheckRowCardProps = {
  row: CheckRow;
  report: DiagnosticsReportResponse;
  trackEvent: ReturnType<typeof useTrackDiagnosticsEvent>;
  print?: boolean;
};

// Each card is a single block-level <div> (not a table row group). break-inside: avoid
// — the print rule that keeps a card from being split across a page break, dropping its
// bottom border (PRE-2920) — is honored on block boxes in every engine, but ignored on a
// <tbody> in Safari/WebKit, so the card must not be table-internal.
export default function DiagnosticsCheckRowCard({ row, report, trackEvent, print }: DiagnosticsCheckRowCardProps) {
  const { token } = theme.useToken();
  const [expanded, setExpanded] = useState(!!print);

  const tip = useMemo(() => {
    const raw = findFixTip(row);
    return raw ? resolveFixTip(raw, row) : null;
  }, [row]);

  const trackingProperties = useMemo(() => {
    const crawlers = getCrawlersTested(report.userAgents);
    const blocked = getBlockedProviders(report.userAgents);
    const allowed = crawlers.filter((c) => !blocked.includes(c));

    return {
      report_id: report.id,
      domain: extractDomain(report.url),
      missing_bots_flagged: blocked.length,
      bots_tested: crawlers.join(', '),
      bots_blocked: blocked.join(', '),
      bots_allowed: allowed.join(', '),
      url_tested: report.url,
      check: row.checkKey,
    };
  }, [report, row.checkKey]);

  const handleToggle = () => {
    const next = !expanded;
    setExpanded(next);
    if (next) {
      trackEvent('Diagnostics: Fix Suggestion Viewed', trackingProperties);
    } else {
      trackEvent('Diagnostics: Fix Suggestion Closed', {
        ...trackingProperties,
        report_generated_timestamp: report.createdAt,
      });
    }
  };

  const hasBlockedCrawlers = row.blockedProviders.length > 0;
  const bg = hasBlockedCrawlers ? token.colorBgContainer : 'rgba(255,255,255,0.5)';
  const headerPadding = hasBlockedCrawlers ? token.paddingMD : `${token.paddingXS}px ${token.paddingMD}px`;

  return (
    <div
      className="diagnostics-check-card"
      style={{
        ...reportCardChrome(token),
        backgroundColor: bg,
        overflow: 'hidden',
        marginBottom: token.marginMD,
      }}
    >
      <Flex align="center" gap={token.marginXS} style={{ padding: headerPadding }}>
        <div style={{ flex: `0 0 ${CHECK_ROW_LABEL_BASIS}` }}>
          <Typography.Text>{row.displayName}</Typography.Text>
        </div>
        <Flex flex="1" gap={token.marginXS} wrap="wrap">
          {row.failed ? (
            <Tag icon={<MinusCircleOutlined />}>Check failed to run</Tag>
          ) : row.blockedProviders.length === 0 ? (
            <Tag icon={<CheckCircleOutlined />} color="success">
              No blocked crawlers
            </Tag>
          ) : (
            row.blockedProviders.map((provider) => (
              <Tag key={provider} icon={<CloseCircleOutlined />} style={blockedCrawlerTagStyle}>
                {provider}
              </Tag>
            ))
          )}
        </Flex>
        {tip && !print && <CheckRowFixToggle expanded={expanded} onToggle={handleToggle} />}
      </Flex>
      {tip && expanded && (
        <div style={{ padding: `0 ${token.paddingMD}px ${token.paddingMD}px` }}>
          <CheckRowFixPanel
            tip={tip}
            expanded={expanded}
            onDocsLinkClick={(link) =>
              trackEvent('Diagnostics: Fix Tip Doc Link Clicked', {
                ...trackingProperties,
                link_url: link.url,
              })
            }
          />
        </div>
      )}
    </div>
  );
}
